<script>
    import { onMount } from 'svelte';
    import { channelsAPI } from './api.js';
    import ChannelConfig from './ChannelConfig.svelte';
    import Icon from './lib/Icon.svelte';

    let channels = [];
    let loading = false;
    let error = null;
    let selectedChannel = null;
    let showCreateModal = false;
    let showWidgetCodeModal = false;
    let widgetCodeChannel = null;

    // Form data for new channel
    let formData = {
        name: '',
        channel_type: 'WEB_WIDGET',
        description: '',
        greeting_enabled: true,
        greeting_message: '¡Hola! ¿En qué puedo ayudarte?',
        enable_auto_ai: false,
        default_chatbot_id: null
    };

    // iconName maps to the SVG <Icon> set (brand glyphs where available, generic
    // Lucide icons otherwise). color is used for the tinted icon chip/accents.
    const channelTypes = [
        { value: 'WEB_WIDGET', label: 'Web Widget', iconName: 'message-square', color: '#2196f3' },
        { value: 'WHATSAPP', label: 'WhatsApp API', iconName: 'whatsapp', color: '#25d366', description: 'API oficial de Meta' },
        { value: 'WHATSAPP_QR', label: 'WhatsApp QR', iconName: 'whatsapp', color: '#128c7e', description: 'Conexion via codigo QR' },
        { value: 'FACEBOOK', label: 'Facebook Messenger', iconName: 'facebook', color: '#0084ff' },
        { value: 'INSTAGRAM', label: 'Instagram Direct', iconName: 'instagram', color: '#e4405f' },
        { value: 'EMAIL', label: 'Email', iconName: 'mail', color: '#ff9800' },
        { value: 'TELEGRAM', label: 'Telegram', iconName: 'telegram', color: '#0088cc' },
        { value: 'SMS', label: 'SMS', iconName: 'sms', color: '#9c27b0' },
        { value: 'API', label: 'API/Custom', iconName: 'plug', color: '#607d8b' }
    ];

    const statusIcons = {
        'active': { icon: '✓', color: '#4caf50', label: 'Activo' },
        'inactive': { icon: '○', color: '#757575', label: 'Inactivo' },
        'pending': { icon: '⏳', color: '#ff9800', label: 'Pendiente' },
        'error': { icon: '⚠', color: '#f44336', label: 'Error' }
    };

    onMount(() => {
        loadChannels();
    });

    async function loadChannels() {
        loading = true;
        error = null;
        try {
            channels = await channelsAPI.getAll();
        } catch (err) {
            error = err.message || 'Error al cargar canales';
            console.error('Error loading channels:', err);
        } finally {
            loading = false;
        }
    }

    function openCreateModal() {
        formData = {
            name: '',
            channel_type: 'WEB_WIDGET',
            description: '',
            greeting_enabled: true,
            greeting_message: '¡Hola! ¿En qué puedo ayudarte?',
            enable_auto_ai: false,
            default_chatbot_id: null
        };
        showCreateModal = true;
    }

    function closeCreateModal() {
        showCreateModal = false;
    }

    async function createChannel() {
        if (!formData.name || !formData.channel_type) {
            alert('Por favor complete nombre y tipo de canal');
            return;
        }

        loading = true;
        error = null;

        try {
            await channelsAPI.create(formData);
            closeCreateModal();
            await loadChannels();
        } catch (err) {
            error = err.message || 'Error al crear canal';
            console.error('Error creating channel:', err);
        } finally {
            loading = false;
        }
    }

    async function deleteChannel(id) {
        if (!confirm('¿Está seguro de eliminar este canal?')) return;

        loading = true;
        error = null;

        try {
            await channelsAPI.delete(id);
            await loadChannels();
        } catch (err) {
            error = err.message || 'Error al eliminar canal';
            console.error('Error deleting channel:', err);
        } finally {
            loading = false;
        }
    }

    function selectChannel(channel) {
        selectedChannel = channel;
    }

    function closeConfig() {
        selectedChannel = null;
        loadChannels();
    }

    function getChannelTypeInfo(type) {
        return channelTypes.find(t => t.value === type) || channelTypes[0];
    }

    function getStatusInfo(status) {
        return statusIcons[status] || statusIcons['pending'];
    }

    function showWidgetCode(channel) {
        widgetCodeChannel = channel;
        showWidgetCodeModal = true;
    }

    function closeWidgetCodeModal() {
        showWidgetCodeModal = false;
        widgetCodeChannel = null;
    }

    function getWidgetCodeHTML() {
        if (!widgetCodeChannel) return '';

        const apiUrl = window.location.origin;
        return `<!-- Mangu CRM Web Widget -->
<script>
  window.ManguCRM = {
    channelId: ` + widgetCodeChannel.id + `,
    apiUrl: '` + apiUrl + `'
  };
<` + `/script>
<script src="` + apiUrl + `/static/widget.js"><` + `/script>`;
    }

    function copyWidgetCode() {
        const code = getWidgetCodeHTML();
        navigator.clipboard.writeText(code).then(() => {
            alert('¡Código copiado al portapapeles!');
        }).catch(err => {
            console.error('Error al copiar:', err);
            alert('Error al copiar el código. Por favor cópielo manualmente.');
        });
    }
</script>

<style>
    .channels-container {
        padding: 20px;
        max-width: 1400px;
        margin: 0 auto;
        height: 100%;
        overflow-y: auto;
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
    }

    .header h1 {
        margin: 0;
        color: #333;
    }

    .btn {
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.2s;
    }

    .btn-primary {
        background: #2196f3;
        color: white;
    }

    .btn-primary:hover {
        background: #1976d2;
    }

    .btn-secondary {
        background: #757575;
        color: white;
    }

    .btn-secondary:hover {
        background: #616161;
    }

    .btn-success {
        background: #4caf50;
        color: white;
    }

    .btn-success:hover {
        background: #388e3c;
    }

    .btn-danger {
        background: #f44336;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-danger:hover {
        background: #d32f2f;
    }

    .btn-config {
        background: #2196f3;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-config:hover {
        background: #1976d2;
    }

    .btn-widget {
        background: #9c27b0;
        color: white;
        padding: 6px 12px;
        font-size: 12px;
    }

    .btn-widget:hover {
        background: #7b1fa2;
    }

    .error-message {
        background: #ffebee;
        color: #c62828;
        padding: 12px 16px;
        border-radius: 4px;
        margin-bottom: 20px;
        border-left: 4px solid #c62828;
    }

    .info-box {
        background: #e3f2fd;
        border-left: 4px solid #2196f3;
        padding: 12px 16px;
        margin-bottom: 20px;
        border-radius: 4px;
    }

    .info-box p {
        margin: 0;
        color: #0d47a1;
        font-size: 13px;
    }

    .channels-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
        gap: 20px;
        margin-top: 20px;
    }

    .channel-card {
        background: white;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 20px;
        transition: all 0.2s;
        position: relative;
    }

    .channel-card:hover {
        box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        transform: translateY(-2px);
    }

    .channel-header {
        display: flex;
        align-items: center;
        gap: 15px;
        margin-bottom: 15px;
    }

    .channel-icon {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 24px;
    }

    .channel-info {
        flex: 1;
    }

    .channel-name {
        font-size: 18px;
        font-weight: 600;
        color: #333;
        margin: 0 0 5px 0;
    }

    .channel-type {
        font-size: 13px;
        color: #757575;
    }

    .status-badge {
        position: absolute;
        top: 15px;
        right: 15px;
        padding: 4px 12px;
        border-radius: 12px;
        font-size: 11px;
        font-weight: 600;
        display: flex;
        align-items: center;
        gap: 5px;
    }

    .channel-description {
        color: #666;
        font-size: 14px;
        margin-bottom: 15px;
        line-height: 1.5;
    }

    .channel-features {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 15px;
    }

    .feature-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 10px;
        background: #f5f5f5;
        border-radius: 4px;
        font-size: 12px;
        color: #666;
    }

    .feature-badge.enabled {
        background: #e8f5e9;
        color: #2e7d32;
    }

    .channel-actions {
        display: flex;
        gap: 8px;
        justify-content: flex-end;
    }

    .modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
    }

    .modal {
        background: white;
        border-radius: 8px;
        padding: 30px;
        width: 90%;
        max-width: 600px;
        max-height: 90vh;
        overflow-y: auto;
    }

    .modal h2 {
        margin: 0 0 20px 0;
        color: #333;
    }

    .form-group {
        margin-bottom: 18px;
    }

    .form-group label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
        font-size: 14px;
    }

    .form-group input,
    .form-group textarea,
    .form-group select {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        font-family: inherit;
    }

    .form-group textarea {
        min-height: 80px;
        resize: vertical;
    }

    .form-group small {
        display: block;
        margin-top: 5px;
        color: #757575;
        font-size: 12px;
    }

    .checkbox-group {
        display: flex;
        align-items: center;
        gap: 8px;
        margin-bottom: 12px;
    }

    .checkbox-group input[type="checkbox"] {
        width: auto;
        cursor: pointer;
    }

    .checkbox-group label {
        margin: 0;
        cursor: pointer;
    }

    .channel-type-selector {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
        gap: 10px;
        margin-bottom: 20px;
    }

    .type-option {
        padding: 15px;
        border: 2px solid #e0e0e0;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s;
        text-align: center;
    }

    .type-option:hover {
        border-color: #2196f3;
        background: #f5f9ff;
    }

    .type-option.selected {
        border-color: #2196f3;
        background: #e3f2fd;
    }

    .type-option-icon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 8px;
    }

    .type-option-label {
        font-weight: 600;
        color: #333;
        font-size: 13px;
    }

    .modal-actions {
        display: flex;
        gap: 10px;
        justify-content: flex-end;
        margin-top: 25px;
    }

    .loading {
        text-align: center;
        padding: 40px;
        color: #757575;
    }

    .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #757575;
    }

    .empty-state h3 {
        color: #333;
        margin-bottom: 10px;
    }

    /* Widget Code Modal */
    .widget-code-modal {
        max-width: 700px;
    }

    .widget-instructions {
        background: #e3f2fd;
        padding: 12px 16px;
        border-radius: 4px;
        margin: 0 0 20px 0;
        font-size: 14px;
        line-height: 1.5;
    }

    .widget-instructions code {
        background: rgba(0,0,0,0.1);
        padding: 2px 6px;
        border-radius: 3px;
        font-family: 'Courier New', monospace;
    }

    .code-block {
        background: #1e1e1e;
        border-radius: 6px;
        padding: 20px;
        margin-bottom: 20px;
        overflow-x: auto;
    }

    .code-block pre {
        margin: 0;
        color: #d4d4d4;
        font-family: 'Courier New', Consolas, monospace;
        font-size: 13px;
        line-height: 1.6;
    }

    .code-block code {
        color: #d4d4d4;
    }

    .widget-preview-info {
        background: #f5f5f5;
        padding: 16px;
        border-radius: 6px;
        margin-bottom: 20px;
    }

    .widget-preview-info h3 {
        margin: 0 0 12px 0;
        font-size: 14px;
        color: #333;
    }

    .widget-preview-info ul {
        margin: 0;
        padding-left: 20px;
        list-style: none;
    }

    .widget-preview-info li {
        margin-bottom: 8px;
        font-size: 13px;
        color: #555;
    }

    .config-note {
        margin: 12px 0 0 0;
        padding: 8px 12px;
        background: #fff3e0;
        border-left: 3px solid #ff9800;
        font-size: 13px;
        color: #666;
        border-radius: 3px;
    }
</style>

{#if selectedChannel}
    <ChannelConfig channel={selectedChannel} onClose={closeConfig} />
{:else}
    <div class="channels-container">
        <div class="header">
            <h1>🌐 Canales de Comunicación</h1>
            <button class="btn btn-primary" on:click={openCreateModal}>
                + Nuevo Canal
            </button>
        </div>

        {#if error}
            <div class="error-message">{error}</div>
        {/if}

        <div class="info-box">
            <p>Los canales permiten recibir y enviar mensajes desde múltiples plataformas (WhatsApp, Facebook, Instagram, Web Widget, etc.)</p>
        </div>

        {#if loading}
            <div class="loading">Cargando canales...</div>
        {:else if channels.length === 0}
            <div class="empty-state">
                <h3>No hay canales configurados</h3>
                <p>Crea tu primer canal para comenzar a recibir mensajes de múltiples plataformas.</p>
            </div>
        {:else}
            <div class="channels-grid">
                {#each channels as channel}
                    {@const typeInfo = getChannelTypeInfo(channel.channel_type)}
                    {@const statusInfo = getStatusInfo(channel.status)}
                    <div class="channel-card">
                        <div class="status-badge" style="background: {statusInfo.color}15; color: {statusInfo.color}">
                            <span>{statusInfo.icon}</span>
                            <span>{statusInfo.label}</span>
                        </div>

                        <div class="channel-header">
                            <div class="channel-icon" style="background: {typeInfo.color}20;">
                                <Icon name={typeInfo.iconName} size={22} />
                            </div>
                            <div class="channel-info">
                                <h3 class="channel-name">{channel.name}</h3>
                                <div class="channel-type">{typeInfo.label}</div>
                            </div>
                        </div>

                        {#if channel.description}
                            <div class="channel-description">{channel.description}</div>
                        {/if}

                        <div class="channel-features">
                            <span class="feature-badge" class:enabled={channel.greeting_enabled}>
                                {channel.greeting_enabled ? '✓' : '○'} Saludo automático
                            </span>
                            <span class="feature-badge" class:enabled={channel.enable_auto_ai}>
                                {channel.enable_auto_ai ? '✓' : '○'} IA automática
                            </span>
                        </div>

                        <div class="channel-actions">
                            {#if channel.channel_type === 'WEB_WIDGET'}
                                <button class="btn btn-widget" on:click={() => showWidgetCode(channel)}>
                                    📋 Código
                                </button>
                            {/if}
                            <button class="btn btn-config" on:click={() => selectChannel(channel)}>
                                ⚙️ Configurar
                            </button>
                            <button class="btn btn-danger" on:click={() => deleteChannel(channel.id)}>
                                🗑️
                            </button>
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
    </div>
{/if}

<!-- Create Channel Modal -->
{#if showCreateModal}
    <div class="modal-overlay" on:click={closeCreateModal}>
        <div class="modal" on:click|stopPropagation>
            <h2>Crear Nuevo Canal</h2>

            <form on:submit|preventDefault={createChannel}>
                <div class="form-group">
                    <label>Tipo de Canal *</label>
                    <div class="channel-type-selector">
                        {#each channelTypes as type}
                            <div
                                class="type-option"
                                class:selected={formData.channel_type === type.value}
                                on:click={() => formData.channel_type = type.value}
                            >
                                <div class="type-option-icon" style="background: {type.color}18;">
                                    <Icon name={type.iconName} size={20} />
                                </div>
                                <div class="type-option-label">{type.label}</div>
                            </div>
                        {/each}
                    </div>
                </div>

                <div class="form-group">
                    <label for="name">Nombre del Canal *</label>
                    <input
                        id="name"
                        type="text"
                        bind:value={formData.name}
                        placeholder="Ej: WhatsApp Soporte, Facebook Principal"
                        required
                    />
                </div>

                <div class="form-group">
                    <label for="description">Descripción</label>
                    <textarea
                        id="description"
                        bind:value={formData.description}
                        placeholder="Describe el propósito de este canal..."
                    ></textarea>
                </div>

                <div class="checkbox-group">
                    <input
                        id="greeting_enabled"
                        type="checkbox"
                        bind:checked={formData.greeting_enabled}
                    />
                    <label for="greeting_enabled">Enviar saludo automático</label>
                </div>

                {#if formData.greeting_enabled}
                    <div class="form-group">
                        <label for="greeting_message">Mensaje de Saludo</label>
                        <textarea
                            id="greeting_message"
                            bind:value={formData.greeting_message}
                        ></textarea>
                    </div>
                {/if}

                <div class="checkbox-group">
                    <input
                        id="enable_auto_ai"
                        type="checkbox"
                        bind:checked={formData.enable_auto_ai}
                    />
                    <label for="enable_auto_ai">Activar IA automáticamente en nuevas conversaciones</label>
                </div>

                <div class="modal-actions">
                    <button type="button" class="btn btn-secondary" on:click={closeCreateModal}>
                        Cancelar
                    </button>
                    <button type="submit" class="btn btn-success" disabled={loading}>
                        {loading ? 'Creando...' : 'Crear Canal'}
                    </button>
                </div>
            </form>
        </div>
    </div>
{/if}

<!-- Widget Code Modal -->
{#if showWidgetCodeModal && widgetCodeChannel}
    <div class="modal-overlay" on:click={closeWidgetCodeModal}>
        <div class="modal widget-code-modal" on:click|stopPropagation>
            <h2>📋 Código de Instalación - {widgetCodeChannel.name}</h2>

            <p class="widget-instructions">
                Copia este código e insértalo en tu sitio web, justo antes del cierre de la etiqueta <code>&lt;/body&gt;</code>.
            </p>

            <div class="code-block">
                <pre><code>{getWidgetCodeHTML()}</code></pre>
            </div>

            <div class="widget-preview-info">
                <h3>✨ El widget se configurará automáticamente con:</h3>
                <ul>
                    <li>🎨 <strong>Color:</strong> {widgetCodeChannel.widget_color || '#2196f3'}</li>
                    <li>📍 <strong>Posición:</strong> {widgetCodeChannel.widget_position || 'right'}</li>
                    <li>💬 <strong>Texto del botón:</strong> {widgetCodeChannel.widget_bubble_text || '💬 Chat with us'}</li>
                    <li>👋 <strong>Saludo:</strong> {widgetCodeChannel.greeting_enabled ? widgetCodeChannel.greeting_message : 'Desactivado'}</li>
                </ul>
                <p class="config-note">
                    💡 Puedes cambiar estos ajustes en la <strong>Configuración del Canal</strong>
                </p>
            </div>

            <div class="modal-actions">
                <button type="button" class="btn btn-secondary" on:click={closeWidgetCodeModal}>
                    Cerrar
                </button>
                <button type="button" class="btn btn-primary" on:click={copyWidgetCode}>
                    📋 Copiar Código
                </button>
            </div>
        </div>
    </div>
{/if}
